import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '@/hooks/useTelegram';
import { api } from '@/services/api';

interface GameSession {
  id: string;
  name: string | null;
  currentScene: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export function SessionsListPage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();

  const [sessions, setSessions] = useState<GameSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resumingId, setResumingId] = useState<string | null>(null);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await api.get<GameSession[]>('/game/sessions');
      setSessions(response || []);
    } catch (err) {
      console.error('Failed to load sessions:', err);
      setError('Не удалось загрузить историю');
    } finally {
      setIsLoading(false);
    }
  };

  const resumeSession = async (sessionId: string) => {
    try {
      setResumingId(sessionId);
      await api.post(`/game/session/${sessionId}/resume`, {});
      hapticFeedback('success');
      navigate('/adventure');
    } catch (err) {
      console.error('Failed to resume session:', err);
      hapticFeedback('error');
      setError('Не удалось продолжить сессию');
      setResumingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return { text: 'Активно', color: 'bg-green-500/20 text-green-400' };
      case 'paused':
        return { text: 'На паузе', color: 'bg-amber-500/20 text-amber-400' };
      case 'completed':
        return { text: 'Завершено', color: 'bg-gray-500/20 text-gray-400' };
      default:
        return { text: status, color: 'bg-gray-500/20 text-gray-400' };
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="p-4 border-b border-gray-800 flex items-center justify-between">
        <button
          onClick={() => {
            hapticFeedback('light');
            navigate(-1);
          }}
          className="text-gray-400 hover:text-white transition-colors"
        >
          ← Назад
        </button>
        <h1 className="text-lg font-bold text-white">История сессий</h1>
        <div className="w-16" />
      </div>

      {/* Content */}
      <div className="p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-gray-400">Загрузка...</p>
            </div>
          </div>
        ) : error && sessions.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <span className="text-4xl block mb-4">⚠️</span>
              <p className="text-red-400 mb-4">{error}</p>
              <button
                onClick={loadSessions}
                className="px-4 py-2 bg-amber-500 text-black rounded-lg font-medium"
              >
                Повторить
              </button>
            </div>
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12">
            <span className="text-6xl block mb-4">📜</span>
            <h2 className="text-xl font-bold text-white mb-2">Нет сессий</h2>
            <p className="text-gray-400 mb-6">
              Начните своё первое приключение!
            </p>
            <button
              onClick={() => navigate('/adventure')}
              className="px-6 py-3 bg-amber-500 text-black rounded-xl font-medium"
            >
              Начать приключение
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map((session, index) => {
              const badge = getStatusBadge(session.status);
              const isResuming = resumingId === session.id;

              return (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-white truncate">
                        {session.name || 'Безымянное приключение'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {formatDate(session.updatedAt)}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${badge.color}`}>
                      {badge.text}
                    </span>
                  </div>

                  {session.currentScene && (
                    <p className="text-sm text-gray-400 mb-3 line-clamp-2">
                      {session.currentScene}
                    </p>
                  )}

                  {session.status !== 'completed' && (
                    <button
                      onClick={() => resumeSession(session.id)}
                      disabled={isResuming}
                      className="w-full py-2.5 rounded-lg bg-amber-500/20 text-amber-400 font-medium hover:bg-amber-500/30 transition-colors disabled:opacity-50"
                    >
                      {isResuming ? 'Загрузка...' : 'Продолжить'}
                    </button>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
